/**
 * Custom TipTap TableCell extension for QBR tables.
 *
 * Detects cells with special data-qbr-* attributes injected by qbr-table-html.ts
 * and renders interactive React components (score inputs, rationale editors, score badges).
 */
import { TableCell } from "@tiptap/extension-table";
import { createElement } from "react";
import { createRoot, Root } from "react-dom/client";
import { renderIconInCell } from "components/TipTapEditorPlugins/firstColumnIcon";
import { applySecondColumnTruncation } from "components/TipTapEditorPlugins/truncateSecondColumn";
import { applyIntentColumnModal } from "components/TipTapEditorPlugins/intentColumnModal";
import styles from "./QBRTable.module.css";

// ─── React micro-components rendered inside NodeViews ───────────────────────

interface ScoreInputProps {
  value: string;
  rowColor: string;
  onChange: (value: string) => void;
}

function ScoreInput({ value, rowColor, onChange }: ScoreInputProps) {
  return createElement(
    "div",
    { className: styles.scoreCell },
    createElement("input", {
      type: "number",
      min: 0,
      max: 10,
      step: 0.1,
      value: value ?? "",
      className: `${styles.scoreInput} ${value ? styles.scoreInputActive : ""}`,
      style: rowColor ? { backgroundColor: rowColor } : undefined,
      onWheel: (e: React.WheelEvent<HTMLInputElement>) =>
        (e.target as HTMLInputElement).blur(),
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        onChange(e.target.value)
    })
  );
}

interface RationaleDisplayProps {
  value: string;
  onStartEdit: () => void;
}

function RationaleDisplay({ value, onStartEdit }: RationaleDisplayProps) {
  const trimmed = value?.trim() || "";
  return createElement(
    "div",
    {
      className: `${styles.rationaleDisplay} ${!trimmed ? styles.rationaleEmpty : ""}`,
      onDoubleClick: onStartEdit
    },
    createElement("span", null, trimmed || "-"),
    createElement(
      "button",
      {
        type: "button",
        className: styles.rationaleEditButton,
        onClick: onStartEdit,
        "aria-label": "Edit score rationale"
      },
      createElement(
        "svg",
        { viewBox: "0 0 20 20", width: 16, height: 16, "aria-hidden": "true" },
        createElement("path", {
          d: "M4 13.75V16h2.25l8.1-8.1-2.25-2.25L4 13.75Zm11.8-7.55a.6.6 0 0 0 0-.85l-1.15-1.15a.6.6 0 0 0-.85 0l-1.1 1.1 2.25 2.25 1.1-1.1Z",
          fill: "currentColor"
        })
      )
    )
  );
}

interface RationaleInputProps {
  value: string;
  onChange: (value: string) => void;
  onFinish: () => void;
  inputRef: (el: HTMLTextAreaElement | null) => void;
}

function RationaleInput({
  value,
  onChange,
  onFinish,
  inputRef
}: RationaleInputProps) {
  return createElement("textarea", {
    ref: (el: HTMLTextAreaElement | null) => {
      inputRef(el);
      if (el) {
        el.focus();
        const len = el.value.length;
        el.setSelectionRange(len, len);
      }
    },
    className: styles.rationaleInput,
    rows: 2,
    value: value ?? "",
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) =>
      onChange(e.target.value),
    onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        onFinish();
      }
    },
    onBlur: onFinish,
    placeholder: ""
  });
}

interface ScoreBadgeProps {
  value: number | null;
  badgeClass: string;
  badgeColor: string;
  rationale?: string;
}

function ScoreBadge({
  value,
  badgeClass,
  badgeColor,
  rationale
}: ScoreBadgeProps) {
  if (value === null) {
    return createElement("span", { className: styles.rationaleEmptyText }, "-");
  }

  const badgeEl = createElement(
    "span",
    {
      className: `${styles.scoreBadge} ${badgeClass}`,
      style: badgeColor ? { backgroundColor: badgeColor } : undefined
    },
    value.toFixed(1)
  );

  if (!rationale?.trim()) {
    return badgeEl;
  }

  // Wrap in hover container with rationale bubble
  return createElement(
    "div",
    { className: styles.rationaleHoverCell },
    badgeEl,
    createElement(
      "div",
      { className: `${styles.rationaleBubble} qbr-rationale-bubble` },
      createElement("div", { className: styles.rationaleBubbleText }, rationale)
    )
  );
}

interface SummaryBadgeProps {
  value: number;
  badgeClass: string;
  badgeColor?: string;
  decimals?: number;
}

function SummaryBadge({
  value,
  badgeClass,
  badgeColor,
  decimals = 1
}: SummaryBadgeProps) {
  return createElement(
    "span",
    {
      className: `${styles.scoreBadge} ${badgeClass}`,
      style: badgeColor ? { backgroundColor: badgeColor } : undefined
    },
    value.toFixed(decimals)
  );
}

// ─── Callback types passed via extension options ────────────────────────────

export interface QBRCellCallbacks {
  getScore: (rowIndex: number) => string;
  getRationale: (rowIndex: number) => string;
  setScore: (rowIndex: number, value: string) => void;
  setRationale: (rowIndex: number, value: string) => void;
  getRowColor: (scoreValue: string) => string;
  getEditingRow: () => number | null;
  setEditingRow: (row: number | null) => void;
}

export interface TeamCellCallbacks {
  owners: {
    owner_name: string;
    scores_list: Record<string, { score: string; rationale: string }>;
    active: boolean;
  }[];
  getRowColor: (scoreValue: string) => string;
  getScoreValue: (
    scoresList: Record<string, { score: string }>,
    rowIndex: number
  ) => number | null;
  getScoreBadgeClass: (value?: string | number) => string;
  getScoreBadgeColor: (value?: string | number) => string;
  getVarianceColor: (value: number) => string;
  getRowStats: (rowIndex: number) => { avg: number; variance: number };
  getOwnerAverage: (scoresList?: Record<string, { score: string }>) => number;
  getObjectiveAvgAverage: () => number;
  getObjectiveVarAverage: () => number;
}

// ─── The extension ──────────────────────────────────────────────────────────

export const QBRTableCell = TableCell.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      "data-qbr-score": { default: null },
      "data-qbr-rationale": { default: null },
      "data-qbr-team-score": { default: null },
      "data-qbr-owner-index": { default: null },
      "data-qbr-team-avg": { default: null },
      "data-qbr-team-var": { default: null },
      "data-qbr-summary-score": { default: null },
      "data-qbr-summary-label": { default: null },
      "data-qbr-summary-avg": { default: null },
      "data-qbr-summary-var": { default: null }
    };
  },

  addNodeView() {
    return ({ node, getPos, editor, HTMLAttributes }) => {
      const dom = document.createElement("td");
      Object.entries(HTMLAttributes).forEach(([key, value]) => {
        if (value != null) dom.setAttribute(key, value as string);
      });

      let root: Root | null = null;

      const renderReact = (element: React.ReactElement) => {
        dom.contentEditable = "false";
        dom.classList.add(styles.qbrCell);
        if (!root) root = createRoot(dom);
        root.render(element);
        return { dom };
      };

      // ─── Personal QBR: Score input ───
      const scoreAttr = HTMLAttributes["data-qbr-score"];
      if (scoreAttr != null) {
        const rowIndex = Number(scoreAttr);
        const callbacks = this.options["personalCallbacks"] as
          | QBRCellCallbacks
          | undefined;
        if (callbacks) {
          dom.classList.add(styles.scoreCellTd);

          const render = () => {
            renderReact(
              createElement(ScoreInput, {
                value: callbacks.getScore(rowIndex),
                rowColor: callbacks.getRowColor(callbacks.getScore(rowIndex)),
                onChange: (val) => {
                  callbacks.setScore(rowIndex, val);
                  render();
                }
              })
            );
          };
          render();
          // Store re-render fn so editor can trigger updates
          (dom as any).__qbrRender = render;
          return { dom };
        }
      }

      // ─── Personal QBR: Rationale ───
      const rationaleAttr = HTMLAttributes["data-qbr-rationale"];
      if (rationaleAttr != null) {
        const rowIndex = Number(rationaleAttr);
        const callbacks = this.options["personalCallbacks"] as
          | QBRCellCallbacks
          | undefined;
        if (callbacks) {
          dom.classList.add(styles.rationaleCell);

          const render = () => {
            const isEditing = callbacks.getEditingRow() === rowIndex;
            if (isEditing) {
              renderReact(
                createElement(RationaleInput, {
                  value: callbacks.getRationale(rowIndex),
                  onChange: (val) => {
                    callbacks.setRationale(rowIndex, val);
                  },
                  onFinish: () => {
                    callbacks.setEditingRow(null);
                    render();
                  },
                  inputRef: () => {}
                })
              );
            } else {
              renderReact(
                createElement(RationaleDisplay, {
                  value: callbacks.getRationale(rowIndex),
                  onStartEdit: () => {
                    callbacks.setEditingRow(rowIndex);
                    render();
                  }
                })
              );
            }
          };
          render();
          (dom as any).__qbrRender = render;
          return { dom };
        }
      }

      // ─── Team QBR: Owner score cell ───
      const teamScoreAttr = HTMLAttributes["data-qbr-team-score"];
      const ownerIndexAttr = HTMLAttributes["data-qbr-owner-index"];
      if (teamScoreAttr != null && ownerIndexAttr != null) {
        const rowIndex = Number(teamScoreAttr);
        const ownerIndex = Number(ownerIndexAttr);
        const callbacks = this.options["teamCallbacks"] as
          | TeamCellCallbacks
          | undefined;
        if (callbacks) {
          dom.classList.add(styles.qbrCellCenter);

          const render = () => {
            const owner = callbacks.owners[ownerIndex];
            const scoreValue = callbacks.getScoreValue(
              owner.scores_list,
              rowIndex
            );
            const rationale = owner.scores_list?.[rowIndex]?.rationale || "";
            renderReact(
              createElement(ScoreBadge, {
                value: scoreValue,
                badgeClass: callbacks.getScoreBadgeClass(scoreValue),
                badgeColor: callbacks.getScoreBadgeColor(scoreValue),
                rationale
              })
            );
          };
          render();
          (dom as any).__qbrRender = render;
          return { dom };
        }
      }

      // ─── Team QBR: Objective Avg ───
      const teamAvgAttr = HTMLAttributes["data-qbr-team-avg"];
      if (teamAvgAttr != null) {
        const rowIndex = Number(teamAvgAttr);
        const callbacks = this.options["teamCallbacks"] as
          | TeamCellCallbacks
          | undefined;
        if (callbacks) {
          dom.classList.add(styles.qbrCellCenter);
          const { avg } = callbacks.getRowStats(rowIndex);
          return renderReact(
            createElement(SummaryBadge, {
              value: avg,
              badgeClass: callbacks.getScoreBadgeClass(avg),
              badgeColor: callbacks.getScoreBadgeColor(avg)
            })
          );
        }
      }

      // ─── Team QBR: Objective Var ───
      const teamVarAttr = HTMLAttributes["data-qbr-team-var"];
      if (teamVarAttr != null) {
        const rowIndex = Number(teamVarAttr);
        const callbacks = this.options["teamCallbacks"] as
          | TeamCellCallbacks
          | undefined;
        if (callbacks) {
          dom.classList.add(styles.qbrCellCenter);
          const { variance } = callbacks.getRowStats(rowIndex);
          return renderReact(
            createElement(SummaryBadge, {
              value: variance,
              badgeClass: callbacks.getVarianceColor(variance),
              decimals: 2
            })
          );
        }
      }

      // ─── Team QBR: Summary label ───
      const summaryLabelAttr = HTMLAttributes["data-qbr-summary-label"];
      if (summaryLabelAttr != null) {
        dom.classList.add(styles.qbrCell, styles.summaryLabel);
        dom.textContent = "Average score per person";
        dom.contentEditable = "false";
        return { dom };
      }

      // ─── Team QBR: Summary owner score ───
      const summaryScoreAttr = HTMLAttributes["data-qbr-summary-score"];
      if (summaryScoreAttr != null) {
        const ownerIndex = Number(summaryScoreAttr);
        const callbacks = this.options["teamCallbacks"] as
          | TeamCellCallbacks
          | undefined;
        if (callbacks) {
          dom.classList.add(styles.qbrCellCenter);
          const avg = callbacks.getOwnerAverage(
            callbacks.owners[ownerIndex]?.scores_list
          );
          return renderReact(
            createElement(SummaryBadge, {
              value: avg,
              badgeClass: callbacks.getScoreBadgeClass(avg),
              badgeColor: callbacks.getScoreBadgeColor(avg)
            })
          );
        }
      }

      // ─── Team QBR: Summary avg ───
      const summaryAvgAttr = HTMLAttributes["data-qbr-summary-avg"];
      if (summaryAvgAttr != null) {
        const callbacks = this.options["teamCallbacks"] as
          | TeamCellCallbacks
          | undefined;
        if (callbacks) {
          dom.classList.add(styles.qbrCellCenter);
          const avg = callbacks.getObjectiveAvgAverage();
          return renderReact(
            createElement(SummaryBadge, {
              value: avg,
              badgeClass: callbacks.getScoreBadgeClass(avg),
              badgeColor: callbacks.getScoreBadgeColor(avg)
            })
          );
        }
      }

      // ─── Team QBR: Summary var ───
      const summaryVarAttr = HTMLAttributes["data-qbr-summary-var"];
      if (summaryVarAttr != null) {
        const callbacks = this.options["teamCallbacks"] as
          | TeamCellCallbacks
          | undefined;
        if (callbacks) {
          dom.classList.add(styles.qbrCellCenter);
          const v = callbacks.getObjectiveVarAverage();
          return renderReact(
            createElement(SummaryBadge, {
              value: v,
              badgeClass: callbacks.getVarianceColor(v),
              decimals: 2
            })
          );
        }
      }

      // ─── Default: first-column icon or regular content ───
      const pos = typeof getPos === "function" ? getPos() : null;
      const columnIndex =
        pos !== null && pos !== undefined
          ? editor.state.doc
              .resolve(pos)
              .index(editor.state.doc.resolve(pos).depth)
          : -1;

      if (columnIndex === 0) {
        dom.contentEditable = "false";
        if (renderIconInCell(dom, node.textContent)) {
          return { dom };
        }
      }

      const contentDOM = document.createElement("div");

      if (columnIndex === 1) {
        return applySecondColumnTruncation(dom, contentDOM);
      }

      // Intent column: truncate + click to open modal
      const intentSpec = applyIntentColumnModal(
        dom,
        contentDOM,
        editor,
        pos,
        columnIndex
      );
      if (intentSpec) {
        return intentSpec;
      }

      dom.appendChild(contentDOM);
      return { dom, contentDOM };
    };
  }
});
